<template>
    <div class="d-flex justify-content-center flex-column">
        <div
            class="alert alert-success mt-3"
            role="alert"
            v-if="sucessMsg"
            v-html="saveSuccessMessage"
            ref="alertBox"
        ></div>
        <div
            class="alert alert-danger mt-3"
            role="alert"
            v-if="validationError"
            v-html="saveErrorMessage"
            ref="alertBox"
        ></div>
        {{ label }}
        <div v-for="(dropdown, index) in dropdowns" :key="index">
            <div class="container" :id="`container${index}`">
                <label
                    :for="dropdown.Label"
                    class="pmm-dropdown-header mt-3 h5"
                    >{{ dropdown.Label }}</label
                >

                <div
                    class="dropdown pmm-custom-dropdown p-0 m-0 mt-2"
                    :class="{ 'dropdown-expanded': dropdown.IsDropdownOpen }"
                >
                    <button
                        class="w-100 dropdown-toggle"
                        type="button"
                        @click="toggleDropdown(dropdown, index)"
                        :aria-expanded="dropdown.IsDropdownOpen"
                        :aria-labelledby="`dropdown-header-${index}`"
                        :class="{
                            'has-selected-items':
                                dropdown.SelectedItems.length > 0,
                            'dropdown-collapse': dropdown.IsDropdownOpen,
                            'dropdown-expand': !dropdown.IsDropdownOpen,
                        }"
                        :id="dropdown.Label"
                    >
                        <span
                            class="badge-section"
                            v-if="dropdown.SelectedItems.length > 0"
                        >
                            <span
                                v-for="(item, idx) in dropdown.SelectedItems"
                                :key="idx"
                                class="badge badge-primary"
                                :aria-label="`${item} close button`"
                            >
                                <span v-html="item"></span>
                                <span
                                    class="badge badge-light close-button"
                                    @click.stop="removeItem(dropdown, item)"
                                    @keydown.enter.stop="
                                        removeItem(dropdown, item)
                                    "
                                    @keydown.space.stop.prevent="
                                        removeItem(dropdown, item)
                                    "
                                    role="button"
                                    tabindex="0"
                                    :aria-label="`Remove ${item} from the dropdown`"
                                    >&times;</span
                                >
                            </span>
                        </span>
                        <span v-else :for="dropdown.Label">Select Items</span>
                    </button>

                    <div
                        class="dropdown-menu w-100"
                        :class="{ show: dropdown.IsDropdownOpen }"
                        :aria-labelledby="`dropdown-header-${index}`"
                    >
                        <input
                            type="text"
                            class="searchInput form-control mb-2"
                            placeholder="Search..."
                            v-model="dropdown.SearchQuery"
                        />

                        <form class="checkboxForm">
                            <!-- Parent Option Rendering -->
                            <div
                                class="form-check"
                                v-for="(option, optionIndex) in filteredOptions(
                                    dropdown
                                )"
                                :key="option.Value"
                            >
                                <div class="custom-checkbox">
                                    <input
                                        class="form-check-input custom-dropdown-checkbox"
                                        type="checkbox"
                                        :value="option.Name"
                                        :id="option.Value"
                                        :aria-labelledby="`label-${option.Value}`"
                                        v-model="dropdown.SelectedItems"
                                        @change="
                                            handleParentCheckboxChange(option)
                                        "
                                    />
                                    <label
                                        class="parent-name"
                                        :for="option.Name"
                                        :id="`label-${option.Value}`"
                                        v-html="option.Name"
                                    >
                                    </label>
                                    <span
                                        v-if="
                                            option.Children &&
                                            option.Children.length > 0
                                        "
                                        :class="
                                            option.expanded
                                                ? 'icon-collapse'
                                                : 'icon-expand'
                                        "
                                        @click.stop="
                                            toggleParent(option, dropdown)
                                        "
                                    >
                                    </span>
                                </div>

                                <!-- Child Options -->
                                <div
                                    v-if="option.expanded"
                                    style="padding-left: 20px"
                                >
                                    <div
                                        class="form-check"
                                        v-for="(
                                            childOption, childIndex
                                        ) in option.Children"
                                        :key="childOption.Value"
                                    >
                                        <div class="custom-checkbox">
                                            <input
                                                class="form-check-input custom-dropdown-checkbox"
                                                type="checkbox"
                                                :value="childOption.Name"
                                                :id="childOption.Value"
                                                v-model="dropdown.SelectedItems"
                                                :aria-labelledby="`label-${childOption.Value}`"
                                                @change="
                                                    handleSubCheckboxChange(
                                                        childOption,
                                                        option
                                                    )
                                                "
                                            />
                                            <label
                                                class="child-name"
                                                :for="childOption.Name"
                                                :id="`label-${childOption.Value}`"
                                                v-html="childOption.Name"
                                            >
                                            </label>
                                            <span
                                                v-if="
                                                    childOption.Children &&
                                                    childOption.Children
                                                        .length > 0
                                                "
                                                :class="
                                                    childOption.expanded
                                                        ? 'icon-collapse'
                                                        : 'icon-expand'
                                                "
                                                @click.stop="
                                                    toggleSub(
                                                        childOption,
                                                        dropdown
                                                    )
                                                "
                                            >
                                            </span>
                                        </div>

                                        <!-- Sub-child Options -->
                                        <div
                                            v-if="childOption.expanded"
                                            style="padding-left: 20px"
                                        >
                                            <div
                                                class="form-check"
                                                v-for="(
                                                    subChildOption,
                                                    subChildIndex
                                                ) in childOption.Children"
                                                :key="subChildOption.Value"
                                            >
                                                <input
                                                    class="form-check-input custom-dropdown-child"
                                                    type="checkbox"
                                                    :value="subChildOption.Name"
                                                    :id="subChildOption.Value"
                                                    v-model="
                                                        dropdown.SelectedItems
                                                    "
                                                    :aria-labelledby="`label-${subChildOption.Value}`"
                                                    @change="
                                                        handleSemiSubCheckboxChange(
                                                            subChildOption,
                                                            childOption
                                                        )
                                                    "
                                                />
                                                <label
                                                    class="sub-child-name"
                                                    :for="subChildOption.Name"
                                                    :id="`label-${subChildOption.Value}`"
                                                    v-html="subChildOption.Name"
                                                >
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
        <!-- <div
            v-if="validationError"
            class="text-danger d-flex justify-content-center align-center mb-4 mt-2 font-weight-bold"
        >
            {{ saveErrorMessage }}
        </div> -->
        <div class="modal-footer d-flex justify-content-center">
            <button
                type="button"
                class="btn btn-danger"
                @click="submit($event)"
            >
                Submit
            </button>
        </div>
        <!-- <div
            v-if="sucessMsg"
            class="d-flex justify-content-center align-center mb-4 mt-2 font-weight-bold text-success"
            v-html="saveSuccessMessage"
        ></div> -->
        <!-- <div
            v-if="errorMsg"
            class="text-danger d-flex justify-content-center align-center mb-4 mt-2 font-weight-bold"
            v-html="saveErrorMessage"
        ></div> -->
    </div>
</template>

<script>
import axios from "axios";

export default {
    props: {
        label: {
            type: String,
            default: "",
        },
        dropdowndata: {
            type: String,
            default: () => [],
        },
        savePreferencesEndpoint: {
            type: String,
            default: "",
        },
        getPreferencesEndpoint: {
            type: String,
            default: "",
        },
        saveSuccessMessage: {
            type: String,
            default: "",
        },
        saveErrorMessage: {
            type: String,
            default: "",
        },
        refresh: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            modalVisible: true,
            dropdowns: JSON.parse(this.dropdowndata),
            sucessMsg: false,
            errorMsg: false,
            validationError: false,
            dropdownData: {
                SsoId: null,
                UserId: null,
                BroadAreaScience: [
                    {
                        id: "label-959e2351-2ff7-47c5-9040-71f1c9c56417",
                    },
                ],
                Degrees: [
                    {
                        id: "aeab1b33-6efb-4eeb-8c19-477f79a64bb5",
                    },
                    {
                        id: "9b7616c3-048f-45c9-9e92-498e006845f4",
                    },
                ],
                Role: [
                    {
                        id: "6094d535-2233-40d4-850c-7c9f84075602",
                    },
                ],
                Expertise: [
                    {
                        id: "4c40bc8c-7b4b-403b-aa72-51357dfc41f5",
                    },
                ],
                Interest: [
                    {
                        id: "4c40bc8c-7b4b-403b-aa72-51357dfc41f5",
                    },
                ],
            },
        };
    },
    created() {
        this.dropdowns = this.dropdowns.map((dropdown) => ({
            ...dropdown,
            isOpen: false, // Ensure every dropdown has isOpen property set to false
        }));
    },
    mounted() {
        this.initializeDropdownSelections();
        console.log("dropdowns", this.dropdowns);
    },

    methods: {
        initializeDropdownSelections() {
            axios
            .get(`${this.getPreferencesEndpoint}`)
            .then((res) => {
                this.dropdowns.forEach((dropdown) => {
                    console.log("dropdownLab", dropdown.Label, dropdown);
                    // Map the API label to your internal label
                    const normalizedLabel =
                        dropdown.Label === "Area of Science"
                            ? "BroadAreaScience"
                            : dropdown.Label;

                    const preSelectedItems = res.data[normalizedLabel];
                    if (preSelectedItems && preSelectedItems.length) {
                        dropdown.SelectedItems = preSelectedItems.map(
                            (preSelected) => {
                                const itemId = preSelected.id;

                                // Helper function to find item recursively
                                const findItemById = (items) => {
                                    for (const item of items) {
                                        if (item.Value === itemId) {
                                            return item; // Found the item
                                        }
                                        // Check children recursively if present
                                        if (
                                            item.Children &&
                                            item.Children.length > 0
                                        ) {
                                            const childMatch = findItemById(
                                                item.Children
                                            );
                                            if (childMatch) return childMatch;
                                        }
                                    }
                                    return null; // Item not found in current level
                                };

                                const matchedItem = findItemById(
                                    dropdown.Values
                                );

                                if (matchedItem) {
                                    return matchedItem.Name;
                                } else {
                                    console.warn(
                                        `Item with id ${itemId} not found for dropdown ${dropdown.Label}`
                                    );
                                    return { id: itemId }; // Optional: Return ID alone if item not found
                                }
                            }
                        );
                    } else {
                        console.warn(
                            `No pre-selected items found for dropdown Label ${dropdown.Label}`
                        );
                    }
                });
            })
            .catch((err) => {
                this.errorMsg = true;
                console.error(err);
            });
        },

        scrollToTop() {
            window.scrollTo({ top: 150, behavior: "smooth" });
        },

        toggleDropdown(dropdown, index) {
            // Toggle the dropdown's open/close state
            dropdown.IsDropdownOpen = !dropdown.IsDropdownOpen;

            // Construct the container ID dynamically based on the index
            const currentContainerId = `container${index + 1}`;
            const currentContainer =
                document.getElementById(currentContainerId);

            // Check if the dropdown is open or closed and set the container border accordingly
            if (dropdown.IsDropdownOpen) {
                // When the dropdown is open, remove the border
                if (currentContainer) {
                    //  currentContainer.style.border = "2px solid red";
                    currentContainer.style.transition =
                        "height 0.3s ease-in-out";
                    currentContainer.style.marginTop = "270px";
                }
            } else {
                // When the dropdown is closed, apply the red border
                if (currentContainer) {
                    currentContainer.style.border = "none";
                    currentContainer.style.transition =
                        "height 0.3s ease-in-out";
                    currentContainer.style.marginTop = "auto";
                }
            }
        },

        toggleExpand(option) {
            option.expanded = !option.expanded;
        },

        toggleSub(option, label) {
            console.log("Sub-option to toggle:", option);

            // Recursive function to find and toggle the specific option and its children in the original dropdowns
            const findAndToggleSub = (items) => {
                items.forEach((item) => {
                    if (
                        item.Name === option.Name &&
                        item.Value === option.Value
                    ) {
                        // Toggle the current option's expanded state
                        if (!item.hasOwnProperty("expanded")) {
                            this.$set(item, "expanded", false);
                        }
                        this.$set(item, "expanded", !item.expanded);
                        console.log(
                            `Toggled expanded for: ${item.Name}, new value: ${item.expanded}`
                        );

                        // If the option has children, toggle their expanded states as well
                        if (item.Children && item.Children.length > 0) {
                            item.Children.forEach((child) => {
                                if (!child.hasOwnProperty("expanded")) {
                                    this.$set(child, "expanded", false);
                                }
                                this.$set(child, "expanded", !child.expanded);
                                console.log(
                                    `Toggled expanded for child: ${child.Name}, new value: ${child.expanded}`
                                );
                            });
                        }
                    }

                    // Recur for nested children
                    if (item.Children && item.Children.length > 0) {
                        findAndToggleSub(item.Children);
                    }
                });
            };

            // Find the dropdown with the matching label and toggle the sub-option
            this.dropdowns.forEach((dropdown) => {
                if (dropdown.Label === label.Label) {
                    findAndToggleSub(dropdown.Values);
                }
            });
        },

        toggleParent(option, label) {
            console.log("Option to toggle:", option);

            // Recursive function to find and toggle the corresponding option in the original dropdowns
            const findAndToggle = (items) => {
                items.forEach((item) => {
                    if (
                        item.Name === option.Name &&
                        item.Value === option.Value
                    ) {
                        // Use $set to toggle the "expanded" property reactively
                        if (!item.hasOwnProperty("expanded")) {
                            this.$set(item, "expanded", false);
                        }
                        this.$set(item, "expanded", !item.expanded);
                        console.log(
                            `Toggled expanded for: ${item.Name}, new value: ${item.expanded}`
                        );
                    }

                    // Recur for children if present
                    if (item.Children && item.Children.length > 0) {
                        findAndToggle(item.Children);
                    }
                });
            };

            // Find the dropdown with the matching label and toggle the option
            this.dropdowns.forEach((dropdown) => {
                if (dropdown.Label === label.Label) {
                    findAndToggle(dropdown.Values);
                }
            });
        },

        filteredOptions(dropdown) {
            const query = dropdown.SearchQuery.toLowerCase().trim(); // Normalize the search query
            if (!query) {
                return dropdown.Values; // Return all options if the search query is empty
            }
            return this.filterOptionsRecursively(dropdown.Values, query);
        },

        // Recursive method to filter options and their children
        filterOptionsRecursively(options, query) {
            return options
            .map((option) => {
                const matches = option.Name.toLowerCase().includes(query); // Check if this option matches

                // Recursively filter the children, if any
                const filteredChildren = option.Children
                    ? this.filterOptionsRecursively(option.Children, query)
                    : [];

                // Include this option if it matches or has matching children
                if (matches || filteredChildren.length > 0) {
                    return {
                        ...option, // Copy the option
                        Children: filteredChildren, // Keep the filtered children
                    };
                }

                return null; // Exclude options that don't match
            })
            .filter(Boolean); // Remove null values
        },

        handleParentCheckboxChange(option) {
            // If the option has children, we handle selection of all children
            if (option.Children && option.Children.length > 0) {
                if (this.isOptionSelected(option)) {
                    // If parent is selected, ensure all children are also selected
                    option.Children.forEach((child) => {
                        if (!this.dropdown.SelectedItems.includes(child.Name)) {
                            this.dropdown.SelectedItems.push(child.Name);
                        }
                    });
                } else {
                    // If parent is deselected, remove all children from selection
                    option.Children.forEach((child) => {
                        const index = this.dropdown.SelectedItems.indexOf(
                            child.Name
                        );
                        if (index !== -1)
                            this.dropdown.SelectedItems.splice(index, 1);
                    });
                }
            } else {
                // If no children, toggle the parent option itself in SelectedItems
                const index = this.dropdown.SelectedItems.indexOf(option.Name);
                if (index === -1) {
                    this.dropdown.SelectedItems.push(option.Name);
                } else {
                    this.dropdown.SelectedItems.splice(index, 1);
                }
            }
        },
        isOptionSelected(option) {
            // Check if an option (and all its children) are selected
            if (option.Children && option.Children.length > 0) {
                return option.Children.every((child) =>
                    this.dropdown.SelectedItems.includes(child.Name)
                );
            } else {
                return this.dropdown.SelectedItems.includes(option.Name);
            }
        },

        handleSubCheckboxChange(subOption, parentOption) {
            subOption.expanded = true;

            const dropdown = this.dropdowns.find((d) =>
                d.Values.includes(parentOption)
            );
            if (!parentOption.Children || parentOption.Children.length === 0) {
                const index = dropdown.SelectedItems.indexOf(subOption.Name);
                if (index > -1) dropdown.SelectedItems.splice(index, 1);
                else console.log("subOption", subOption);
                dropdown.SelectedItems.push(subOption.Name);
            }
        },

        // Expand and select the semi-sub-options independently
        handleSemiSubCheckboxChange(semiSubOption, subOption) {
            console.log("subOption", subOption.Value);
            const dropdown = this.dropdowns.find((d) =>
                d.Values.includes(subOption)
            );
            console.log("dropdown", dropdown);
            const index = dropdown.SelectedItems.indexOf(semiSubOption.Name);
            if (index > -1) dropdown.SelectedItems.splice(index, 1);
            else dropdown.SelectedItems.push(semiSubOption.Name);
        },

        removeItem(dropdown, item) {
            const index = dropdown.SelectedItems.indexOf(item);
            if (index > -1) {
                dropdown.SelectedItems.splice(index, 1);
            }

            // Deselect the corresponding checkboxes in the dropdown options
            dropdown.Values.forEach((option) => {
                if (option.Name === item) {
                    option.selected = false; // Deselect parent option
                }

                // Check sub-options
                option.Children.forEach((subOption) => {
                    if (subOption.Name === item) {
                        subOption.selected = false; // Deselect sub-option
                    }

                    // Check semi sub-options
                    subOption.Children.forEach((semiSubOption) => {
                        if (semiSubOption.Name === item) {
                            semiSubOption.selected = false; // Deselect semi sub-option
                        }
                    });
                });
            });
        },

        submit(event) {
            if (event) {
                event.preventDefault();
            }
            const allInputsEmpty = this.dropdowns.every(
                (dropdown) =>
                    !dropdown.SelectedItems ||
                    dropdown.SelectedItems.length === 0
            );

            if (allInputsEmpty) {
                this.validationError = true;
                this.sucessMsg = false;
                this.scrollToTop()
                return; // Stop execution
            } else {
                this.validationError = false;
            }
            let resultObject = {};
            this.dropdowns.forEach((dropdown) => {
                let label = dropdown.Label;
                const selectedItems = dropdown.SelectedItems;
                console.log("Selected items for", label, selectedItems);

                // Rename 'Area of Science' to 'BroadAreaScience'
                if (label === "Area of Science") {
                    label = "BroadAreaScience";
                }
                const selectedValues = [];

                selectedItems.forEach((selected) => {
                    // Validate the selected item in the dropdown values
                    let validatedOption = null;
                    for (let option of dropdown.Values) {
                        validatedOption = this.validateSelectedOption(
                            option,
                            selected
                        );
                        if (validatedOption) break;
                    }

                    if (validatedOption) {
                        // Recursively get selected children
                        const children =
                            this.findSelectedOptions(validatedOption);
                        console.log(
                            "Children for",
                            validatedOption.Name,
                            children
                        );

                        const currentOption = {
                            name: validatedOption.Name,
                            id: validatedOption.Value,
                            children: children.length ? children : [],
                        };

                        selectedValues.push(currentOption);
                    } else {
                        console.log("No matching option found for", selected);
                    }
                });

                if (selectedValues.length) {
                    resultObject[
                        label.charAt(0).toUpperCase() + label.slice(1)
                    ] = selectedValues;
                } else {
                    console.log(`No selected values for ${label}`);
                }
            });

            const output = this.flattenAllCategories(resultObject);
            Object.keys(output).forEach((key) => {
                output[key] = this.removeDuplicates(output[key]);
            });

            axios({
                method: "post",
                url: this.savePreferencesEndpoint,
                headers: { "Content-Type": "application/json" },
                data: JSON.stringify(output, null, 2),
            });
            this.scrollToTop();
            this.sucessMsg = true;
            if (this.refresh === "true") {
                window.location.reload();
                this.setTimeout(() => {
                    this.sucessMsg = false;
                }, 6000);
            }
           
        },

        removeDuplicates(arr) {
            console.log("arr", arr);
            const uniqueItems = new Set();
            return arr.filter((item) => {
                if (!uniqueItems.has(item.id)) {
                    uniqueItems.add(item.id);
                    return true;
                }
                return false;
            });
        },
        flattenAllCategories(data) {
            const result = {};

            // Iterate over each main category key in data (e.g., "Role", "Expertise", "Interest")
            Object.keys(data).forEach((category) => {
                result[category] = [];

                // Recursive function to flatten items
                function extractItems(items) {
                    items.forEach((item) => {
                        // Add the current item (name and id) to the result array for this category
                        const { name, id } = item;
                        result[category].push({ name, id });

                        // If there are children, recursively process them
                        if (
                            Array.isArray(item.children) &&
                            item.children.length > 0
                        ) {
                            extractItems(item.children);
                        }
                    });
                }

                // Start the recursion with the main category array
                if (Array.isArray(data[category])) {
                    extractItems(data[category]);
                }
            });

            return result;
        },

        validateSelectedOption(option, selected) {
            // Check if the option is the selected item
            if (option.Name === selected) {
                console.log("Valid option found:", option);
                return option; // Return the option if it matches the selected item
            }

            // If the option has children, search within them recursively
            if (option.Children && option.Children.length > 0) {
                for (let child of option.Children) {
                    const result = this.validateSelectedOption(child, selected);
                    if (result) {
                        return result; // Return the validated child/sub-child if found
                    }
                }
            }

            // Return null if no match found at this level
            return null;
        },

        findSelectedOptions(option) {
            if (!option.Children || option.Children.length === 0) {
                console.log("Bharath");
                return []; // If no children, return an empty array
            }
            const selectedChildren = option.Children.filter((child) =>
                this.isSelected(child)
            ) 
            .map((child) => {
                const subChildren = this.findSelectedOptions(child); // Recursively get selected sub-options
                const result = {
                    name: child.Name,
                    id: child.Value,
                };
                if (subChildren.length > 0) {
                    result.children = subChildren;
                }
                return result;
            });

            return selectedChildren;
        },
        isSelected(option) {
            return this.dropdowns.some((dropdown) =>
                dropdown.SelectedItems.includes(option.Name)
            );
        },
    },
};
</script>

<style>
.customer-success {
    color: green;
}

.pmm-custom-dropdown .dropdown-toggle::after {
    float: right;
    margin-top: 10px;
    position: relative;
    top: 0px;
    right: auto;
    left: 0;
}

.pmm-custom-dropdown .dropdown-toggle {
    background-color: transparent !important;
    border: 1px solid #d1cccc;
    border-radius: 4px;
    text-align: left;
    padding: 12px;
}

.pmm-custom-dropdown .dropdown-toggle.has-selected-items::after {
    top: -26px;
    /* Adjusted position when SelectedItems is greater than 0 */
}

.pmm-custom-dropdown .btn-primary {
    color: black !important;
    background-color: transparent !important;
    width: 400px;
    border: 2px solid #d1cccc;
    height: auto;
    text-align: left;
}

.pmm-custom-dropdown .btn-primary:hover,
.pmm-custom-dropdown .btn-primary:active {
    color: black;
    background-color: transparent !important;
    width: 400px;
    border: 2px solid #d1cccc;
}

.badge-primary {
    background: #d22338;
    color: white;
    margin-left: 10px !important;
    font-style: normal;
    margin-top: 10px !important;
}

.dropdown-menu {
    width: 400px;
    padding-left: 10px;
    width: 100%;
    height: 250px;
    overflow-y: auto;
}

/* .pmm-dropdown-header:after {
    content: "\2713";
    color: "#d22338";
    margin-left: "10px";
    
} */

.badge-section {
    display: flex;
    flex-wrap: wrap;
}

.dropdown-login-text {
    color: #d22338;
    font-weight: bold;
}

.icon-expand {
    margin-left: 4px;
    margin-top: -10px;
    cursor: pointer;
}

.icon-collapse {
    margin-left: 4px;
    margin-top: -4px;
}

.pmm-custom-dropdown .dropdown-toggle::after {
    content: "";
    display: inline-block;
    width: 8px;
    height: 10px;
    transition: transform 0.3s ease;
}

/* When the dropdown is collapsed */
.pmm-custom-dropdown .dropdown-toggle.dropdown-collapse::after {
    transform: rotate(180deg);
    /* Rotate or change appearance */
}

/* When the dropdown is expanded */
.pmm-custom-dropdown .dropdown-toggle.dropdown-expand::after {
    transform: rotate(0deg);
    /* Original state */
}

.icon-collapse::before {
    content: "▼";
    font-size: 1.2em;
}

.icon-expand::before {
    content: "►";
    font-size: 1.2em;
}

.custom-checkbox {
    display: flex;
    align-items: center;
    cursor: normal;
}

.parent-name {
    margin-top: 4px;
    cursor: normal;
}

.child-name {
    margin-top: 4px;
    cursor: normal;
}

.sub-child-name {
    cursor: normal;
}

.custom-dropdown-checkbox {
    margin-top: -5px;
    cursor: pointer;
}

.badge-section {
    display: flex;
    flex-wrap: wrap; /* Ensure badges wrap if too many */
    gap: 5px;
    max-width: 100%; /* Limit to 100% of the button width */
    overflow-x: auto; /* Allow horizontal scroll if needed */
}

/* Ensure badges are properly sized */
.badge {
    white-space: nowrap; /* Prevent text from breaking inside badges */
    padding: 5px 10px;
    font-size: 12px;
    display: flex;
    align-items: center;
}

/* Close button style */
.badge .close-button {
    margin-left: 5px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
}

/* Mobile-specific styles */
@media (max-width: 576px) {
    .badge-section {
        flex-wrap: wrap; /* Allow badges to wrap */
        max-width: 100%; /* Ensure badges fit within the container */
    }

    .badge {
        max-width: 100%; /* Ensure badges take full width */
        font-size: 12px; /* Smaller font size for mobile */
        margin-bottom: 5px; /* Space between badges */
    }

    .close-button {
        font-size: 1rem; /* Slightly smaller close button on mobile */
    }
}
</style>



// WEBPACK FOOTER //
// src/app/components/phd/aha-search-dropdown.vue